import { useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import type { ResolvedAddress } from "./LocationPicker";

const LIBRARIES: "places"[] = ["places"];

export interface PlaceSelection {
  lat: number;
  lng: number;
  resolved: ResolvedAddress;
  description: string;
}

interface PlacesSearchInputProps {
  onPlaceSelect: (place: PlaceSelection) => void;
}

const parseAddressComponents = (
  components: any[],
): ResolvedAddress => {
  const get = (type: string) =>
    components.find((c: any) => c.types.includes(type))?.long_name || "";

  const streetNumber = get("street_number");
  const route = get("route");
  const addressLine =
    [streetNumber, route].filter(Boolean).join(", ") ||
    get("sublocality_level_1") ||
    get("neighborhood");

  return {
    addressLine,
    city:
      get("locality") ||
      get("sublocality_level_1") ||
      get("administrative_area_level_3"),
    state: get("administrative_area_level_1"),
    district: get("administrative_area_level_2"),
    pincode: get("postal_code"),
    country: get("country") || "India",
  };
};

const PlacesSearchInputInner = ({ onPlaceSelect }: PlacesSearchInputProps) => {
  const [selecting, setSelecting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: { componentRestrictions: { country: "in" } },
    googleMaps: (window as any).google?.maps,
  });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        clearSuggestions();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [clearSuggestions]);

  const handleSelect = async (description: string) => {
    setValue(description, false);
    clearSuggestions();
    setSelecting(true);
    try {
      const results = await getGeocode({ address: description });
      const { lat, lng } = await getLatLng(results[0]);
      const resolved = parseAddressComponents(results[0].address_components);
      onPlaceSelect({ lat, lng, resolved, description });
    } catch {
      // Best-effort — leave the map/pin untouched if geocoding fails.
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="position-relative mb-2" ref={ref}>
      <div className="input-group input-group-outline">
        <input
          type="text"
          className="form-control"
          placeholder="Search for an address, locality or landmark..."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!ready || selecting}
          autoComplete="off"
        />
      </div>
      {status === "OK" && (
        <ul
          className="list-group position-absolute w-100 shadow"
          style={{ zIndex: 9999, top: "100%", maxHeight: 220, overflowY: "auto" }}
        >
          {data.map(({ place_id, description }: any) => (
            <li
              key={place_id}
              className="list-group-item list-group-item-action text-sm"
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(description)}
            >
              {description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PlacesSearchInput = (props: PlacesSearchInputProps) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
  });

  if (loadError || !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return null;
  }

  if (!isLoaded) {
    return (
      <div className="form-control bg-light text-muted text-sm mb-2">
        Loading address search…
      </div>
    );
  }

  return <PlacesSearchInputInner {...props} />;
};

export default PlacesSearchInput;
